import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import type { CombinedForecast } from '@catalyst-psa/shared';

import { Spinner } from '../../components/ui/Spinner.js';
import { OpportunityForecastPanel } from '../opportunities/OpportunityForecastPanel.js';
import { publicApi } from '../project-portal/publicApi.js';
import { AgreementForecastPanel } from './AgreementForecastPanel.js';
import { ForecastSummaryGrids, type ForecastSummaryArea } from './ForecastSummaryGrids.js';
import { ProjectForecastPanel } from './ProjectForecastPanel.js';
import { TicketForecastPanel } from './TicketForecastPanel.js';

/**
 * Public, unauthenticated forecast portal (/f/:token). Renders the same combined Insights
 * forecast — the by-month and by-year totals plus the three detail panels — read-only.
 * No drill-down navigation (the visitor is not signed in).
 */
export function PublicForecastPage(): JSX.Element {
  const { token } = useParams<{ token: string }>();

  const query = useQuery({
    queryKey: ['public-forecast', token],
    queryFn: () => publicApi.get<CombinedForecast>(`/public/forecast/${token ?? ''}`),
    enabled: Boolean(token),
    retry: false,
  });

  const data = query.data;

  const areas: ForecastSummaryArea[] = data
    ? [
        {
          key: 'opportunities',
          label: 'Opportunities',
          totalsByMonth: data.opportunity.totalsByMonth,
          total: data.opportunity.total,
        },
        {
          key: 'agreements',
          label: 'Agreements',
          totalsByMonth: data.agreement.totalsByMonth,
          total: data.agreement.total,
        },
        {
          key: 'projects',
          label: 'Projects',
          totalsByMonth: data.project.totalsByMonth,
          total: data.project.total,
        },
        {
          key: 'tickets',
          label: 'Tickets',
          totalsByMonth: data.ticket.totalsByMonth,
          total: data.ticket.total,
        },
      ]
    : [];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-4">
          <h1 className="text-lg font-semibold text-slate-900">Catalyst — Forecast</h1>
          <p className="text-sm text-slate-500">
            Combined future-revenue forecast across opportunities, agreements, projects, and
            tickets.
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6">
        {query.isLoading ? (
          <div className="flex items-center justify-center gap-3 py-20 text-slate-500">
            <Spinner className="h-6 w-6" />
            <span className="text-sm">Loading forecast…</span>
          </div>
        ) : query.isError || !data ? (
          <div className="mx-auto max-w-md rounded-xl border border-amber-200 bg-amber-50 p-6 text-center">
            <h2 className="text-base font-semibold text-amber-900">Forecast unavailable</h2>
            <p className="mt-2 text-sm text-amber-800">
              This link is no longer active or the address is incorrect.
            </p>
          </div>
        ) : (
          <>
            <ForecastSummaryGrids areas={areas} />
            <OpportunityForecastPanel data={data.opportunity} />
            <AgreementForecastPanel data={data.agreement} interactive={false} />
            <ProjectForecastPanel data={data.project} interactive={false} />
            <TicketForecastPanel data={data.ticket} interactive={false} />
          </>
        )}
      </main>
    </div>
  );
}
